import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneRight, EnvelopeSimple, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

// Strip lightweight markdown so replies read cleanly in the bubble.
const clean = (s) =>
  String(s || "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^\s*[-*]\s+/gm, "• ")
    .replace(/`([^`]+)`/g, "$1")
    .trim();

const SUPPORT_EMAIL = "support@getlcshield.com";

export function SupportChat() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content}
  const scrollRef = useRef(null);

  const pills = [t("chat.pill0"), t("chat.pill1"), t("chat.pill2")];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    const history = messages.slice(-6);
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/support-chat", { message: q, lang, history });
      setMessages((m) => [...m, { role: "assistant", content: clean(r.data.reply) }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: t("chat.error"), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const emailHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("LC Shield enterprise enquiry")}`;

  return (
    <>
      {/* Floating action button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t("chat.open")}
        data-testid="support-chat-fab"
        className="fixed bottom-5 right-5 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-lc-sky text-lc-navy shadow-lg shadow-lc-sky/30 transition-transform duration-200 hover:scale-105 active:scale-95"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} weight="bold" />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <ChatCircleDots size={26} weight="fill" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            data-testid="support-chat-modal"
            className="fixed bottom-24 right-5 z-[90] flex h-[520px] max-h-[75vh] w-[360px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-xl border border-white/10 bg-lc-slate shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-white/10 bg-lc-navy px-4 py-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-lc-sky/15">
                <ShieldCheck size={20} weight="duotone" className="text-lc-sky" />
              </div>
              <div className="min-w-0">
                <p className="font-serif text-sm text-white leading-tight">{t("chat.title")}</p>
                <p className="truncate text-[11px] text-lc-fog">{t("chat.subtitle")}</p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="support-chat-messages">
              <div className="rounded-lg rounded-tl-sm bg-lc-navy/60 px-3 py-2 text-[13px] leading-relaxed text-slate-200">
                {t("chat.greeting")}
              </div>

              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {pills.map((p, i) => (
                    <button
                      key={i}
                      data-testid={`support-pill-${i}`}
                      onClick={() => send(p)}
                      className="rounded-full border border-lc-sky/30 bg-lc-sky/10 px-3 py-1.5 text-[11px] text-lc-sky transition-colors duration-200 hover:bg-lc-sky/20"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div
                    className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "rounded-tr-sm bg-lc-sky text-lc-navy"
                        : m.error
                        ? "rounded-tl-sm bg-lc-critical/15 text-lc-critical"
                        : "rounded-tl-sm bg-lc-navy/60 text-slate-200"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-1 rounded-lg rounded-tl-sm bg-lc-navy/60 px-3 py-2.5">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-lc-sky [animation-delay:-0.3s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-lc-sky [animation-delay:-0.15s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-lc-sky" />
                  </div>
                </div>
              )}
            </div>

            {/* Email fallback */}
            <a
              href={emailHref}
              data-testid="support-email-btn"
              className="flex items-center justify-center gap-1.5 border-t border-white/10 bg-lc-navy/40 py-2 text-[11px] font-semibold text-lc-fog transition-colors duration-200 hover:text-lc-sky"
            >
              <EnvelopeSimple size={13} weight="fill" /> {t("chat.emailBtn")} · {SUPPORT_EMAIL}
            </a>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-white/10 bg-lc-navy px-3 py-2.5"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chat.placeholder")}
                data-testid="support-chat-input"
                className="flex-1 rounded-full border border-white/15 bg-lc-slate px-3.5 py-2 text-[13px] text-white placeholder:text-lc-steel focus:border-lc-sky focus:outline-none"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="support-chat-send"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lc-sky text-lc-navy transition-opacity duration-200 hover:opacity-90 disabled:opacity-40"
              >
                <PaperPlaneRight size={17} weight="fill" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default SupportChat;
